// Audio playback (F-02.11, D-23, S-12): only at 1x forward. The audio clock drives the playhead while audible.

import { ALL_FORMATS, AudioBufferSink, BlobSource, Input, type InputAudioTrack } from 'mediabunny';

export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private input: Input | null = null;
  private track: InputAudioTrack | null = null;
  private sink: AudioBufferSink | null = null;
  private file: File | null = null;
  private nodes = new Set<AudioBufferSourceNode>();
  private runId = 0;
  private startCtxTime = 0;
  private startMediaTime = 0;
  running = false;
  volume = 1;

  async setFile(file: File | null): Promise<boolean> {
    if (file === this.file) return !!this.track;
    this.stop();
    this.input?.dispose();
    this.input = null;
    this.track = null;
    this.sink = null;
    this.file = file;
    if (!file) return false;
    try {
      this.input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
      this.track = await this.input.getPrimaryAudioTrack();
      if (!this.track || !(await this.track.canDecode())) {
        this.track = null;
        return false;
      }
      this.sink = new AudioBufferSink(this.track);
      return true;
    } catch {
      this.track = null;
      return false;
    }
  }

  get available(): boolean {
    return !!this.sink;
  }

  setVolume(v: number): void {
    this.volume = v;
    if (this.gain) this.gain.gain.value = v;
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext({ latencyHint: 'interactive' });
      this.gain = this.ctx.createGain();
      this.gain.gain.value = this.volume;
      this.gain.connect(this.ctx.destination);
    }
    return this.ctx;
  }

  /**
   * Starts playback at the given media time (seconds, the audio track's own timestamps). Returns false when no
   * audio is available. The caller reads `mediaTime()` as the master clock.
   */
  async start(mediaTime: number): Promise<boolean> {
    this.stop();
    if (!this.sink) return false;
    // Take the run id before awaiting: a stop() while the context resumes must cancel this start.
    const run = ++this.runId;
    const ctx = this.ensureContext();
    if (ctx.state === 'suspended') await ctx.resume();
    if (run !== this.runId) return false;
    // Small lead so the first buffers are scheduled before they are due (avoids clicks).
    this.startCtxTime = ctx.currentTime + 0.06;
    this.startMediaTime = mediaTime;
    this.running = true;
    void this.pump(run, mediaTime);
    return true;
  }

  private async pump(run: number, from: number): Promise<void> {
    const ctx = this.ctx!;
    const sink = this.sink!;
    try {
      for await (const { buffer, timestamp } of sink.buffers(Math.max(0, from))) {
        if (run !== this.runId) return;
        const node = ctx.createBufferSource();
        node.buffer = buffer;
        node.connect(this.gain!);
        const at = this.startCtxTime + (timestamp - this.startMediaTime);
        if (at >= ctx.currentTime) node.start(at);
        else {
          const skip = ctx.currentTime - at;
          if (skip < buffer.duration) node.start(ctx.currentTime, skip);
        }
        this.nodes.add(node);
        node.onended = () => this.nodes.delete(node);
        // Stay at most ~1 s ahead of the clock.
        while (run === this.runId && timestamp - this.mediaTime() > 1) await new Promise((r) => setTimeout(r, 100));
      }
    } catch {
      /* decoding stopped or input disposed */
    }
  }

  /** Current media time of the audio clock. */
  mediaTime(): number {
    if (!this.ctx || !this.running) return this.startMediaTime;
    return this.startMediaTime + Math.max(0, this.ctx.currentTime - this.startCtxTime);
  }

  /** True once the audio clock has actually started advancing. */
  get clockStarted(): boolean {
    return !!this.ctx && this.running && this.ctx.currentTime >= this.startCtxTime;
  }

  /** Test hook (S-12): an analyser on the output, used to measure A/V sync. */
  analyser(): AnalyserNode {
    const ctx = this.ensureContext();
    const a = ctx.createAnalyser();
    a.fftSize = 256;
    this.gain!.connect(a);
    return a;
  }

  stop(): void {
    this.runId++;
    this.running = false;
    for (const n of this.nodes) {
      try {
        n.stop();
      } catch {
        /* already stopped */
      }
    }
    this.nodes.clear();
  }

  dispose(): void {
    this.stop();
    this.input?.dispose();
    void this.ctx?.close();
    this.ctx = null;
  }
}
